import { useEffect, useState } from 'react';
import {
    XMarkIcon,
    ArrowDownTrayIcon,
    ExclamationTriangleIcon,
    UserIcon,
    ClockIcon,
    DocumentIcon,
    SpeakerWaveIcon,
} from '@heroicons/react/24/outline';
import axios from '../../axios';

const MEDIA_LABELS = {
    audio: 'Nota de audio',
    image: 'Imagen',
    video: 'Video',
    document: 'Documento',
    sticker: 'Sticker',
};

// Visor unificado para audio/imagen/video/documento de un log de WhatsApp.
// Descarga el binario bajo demanda vía proxy autenticado (mismo endpoint que usa el chat).
const MediaViewerModal = ({ log, onClose }) => {
    const [blobUrl, setBlobUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const message = log?.raw_payload?.message;
    const mediaType = message?.type;
    const caption = message?.[mediaType]?.caption || null;
    const filename = mediaType === 'document' ? (message?.document?.filename || null) : null;

    useEffect(() => {
        let objectUrl = null;
        let cancelado = false;

        const cargarMedia = async () => {
            setLoading(true);
            setError(false);
            try {
                const response = await axios.get(`api/whatsapp/logs/${log.id}/media`, {
                    responseType: 'blob',
                });
                objectUrl = URL.createObjectURL(response.data);
                if (!cancelado) setBlobUrl(objectUrl);
            } catch (err) {
                console.error('Error al cargar multimedia:', err);
                if (!cancelado) setError(true);
            } finally {
                if (!cancelado) setLoading(false);
            }
        };

        cargarMedia();

        return () => {
            cancelado = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [log?.id]);

    if (!log) return null;

    const formatearFecha = (fecha) => {
        if (!fecha) return '-';
        return new Date(fecha).toLocaleString('es-MX', {
            year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
        });
    };

    const nombreRemitente = log.appointment?.nombre_paciente || log.from_number || 'Desconocido';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/70" onClick={onClose} />

            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg mx-auto overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 bg-[#075E54]">
                    <div className="flex items-center gap-2 text-white min-w-0">
                        <UserIcon className="h-5 w-5 shrink-0" />
                        <div className="min-w-0">
                            <p className="text-sm font-semibold truncate">{nombreRemitente}</p>
                            <p className="text-xs text-white/70 flex items-center gap-1">
                                <ClockIcon className="h-3.5 w-3.5" />
                                {formatearFecha(log.created_at)}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-white/80 hover:text-white transition-colors cursor-pointer shrink-0">
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 flex flex-col items-center justify-center bg-gray-50 min-h-55">
                    {loading && (
                        <div className="flex flex-col items-center gap-2 text-gray-500">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#25D366]"></div>
                            <p className="text-sm">Cargando {MEDIA_LABELS[mediaType] || 'archivo'}...</p>
                        </div>
                    )}

                    {!loading && error && (
                        <div className="flex flex-col items-center gap-2 text-red-500 text-center">
                            <ExclamationTriangleIcon className="h-10 w-10" />
                            <p className="text-sm font-medium">Archivo ya no disponible</p>
                            <p className="text-xs text-gray-500">El contenido multimedia expiró en los servidores de WhatsApp.</p>
                        </div>
                    )}

                    {!loading && !error && mediaType === 'image' && (
                        <img src={blobUrl} alt="Imagen enviada" className="max-h-[60vh] max-w-full rounded-lg shadow-sm" />
                    )}

                    {!loading && !error && mediaType === 'sticker' && (
                        <img src={blobUrl} alt="Sticker enviado" className="max-h-64 max-w-full" />
                    )}

                    {!loading && !error && mediaType === 'audio' && (
                        <div className="flex flex-col items-center gap-3 w-full">
                            <div className="w-16 h-16 rounded-full bg-[#25D366]/10 flex items-center justify-center">
                                <SpeakerWaveIcon className="h-8 w-8 text-[#25D366]" />
                            </div>
                            <audio controls autoPlay src={blobUrl} className="w-full max-w-sm" />
                        </div>
                    )}

                    {!loading && !error && mediaType === 'video' && (
                        <video controls autoPlay src={blobUrl} className="max-h-[60vh] max-w-full rounded-lg shadow-sm" />
                    )}

                    {!loading && !error && mediaType === 'document' && (
                        <div className="flex flex-col items-center gap-3">
                            <DocumentIcon className="h-16 w-16 text-gray-400" />
                            <p className="text-sm text-gray-700">{filename || 'Documento'}</p>
                        </div>
                    )}

                    {!loading && !error && caption && (
                        <p className="text-sm text-gray-700 mt-3 text-center">{caption}</p>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-2 px-4 py-3 bg-white border-t border-gray-100">
                    {!loading && !error && blobUrl && (
                        <a
                            href={blobUrl}
                            download={filename || `whatsapp-${mediaType}-${log.id}`}
                            className="flex items-center gap-2 px-4 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                        >
                            <ArrowDownTrayIcon className="h-4 w-4" />
                            Descargar
                        </a>
                    )}
                    <button
                        onClick={onClose}
                        className="px-4 py-1.5 text-sm bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors cursor-pointer"
                    >
                        Cerrar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MediaViewerModal;
